import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { isBefore } from "date-fns";
import { Button, Icon, Popup } from "semantic-ui-react";
import { styled } from "styled-components";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { formatDate } from "simplydo/core/dates";
import { ideaCoverImage } from "simplydo/core/images";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { LabelItem } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";
import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips";
import TagModalChooser, {
  TagView,
} from "components/lib/Choosers/Tags/TagModalChooser";
import CircularProgress from "components/lib/CircularProgress";
import ConfigurableTable from "components/lib/ConfigurableTable";
import type { ActionDropdownProps } from "components/lib/ConfigurableTable/ActionDropdown";
import { IdeaLikeHandler } from "components/lib/Ideas";
import { LaneCardImpacts } from "components/lib/ProjectBoard/LaneCardImpacts";
import { PlaceholderItem, TooltipButton } from "components/lib/UI";

import { IdeaAssessmentProgress } from "../IdeaAssessmentProgress";
import IdeaDisplayFields from "../IdeaDisplayFields";
import { addTag, removeTag, voteForIdea } from "./ideaViewActions";

const CoverImageCell = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
`;

const CoverMetaPins = styled.div`
  position: absolute;
  top: -4px;
  left: -4px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 0;
  pointer-events: none;

  i {
    margin: 0 !important;
  }

  > * {
    pointer-events: auto;
  }
`;

const IdeaName = styled.h4`
  max-width: 100%;
  margin-bottom: 2px;
  margin-top: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

type IdeaTableViewProps = {
  ideas: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"];
  challenge: OpenAPI.Schemas["Challenge"];
  loading: boolean;
  onSelect: Dispatch<
    SetStateAction<OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"]>
  >;
  selectedIdeas: string[];
  ideasVotedFor: number;
  ideaActions: ActionDropdownProps<
    OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][number]
  >["actions"];
  setPreviewOpen: (ideaId: string, options?: { toComments?: boolean }) => void;
};

const IdeaTableView = ({
  ideas,
  challenge,
  loading,
  onSelect,
  selectedIdeas,
  ideasVotedFor,
  ideaActions,
  setPreviewOpen,
}: IdeaTableViewProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const [tagsOpen, setTagsOpen] = useState("");

  const canManageChallenges = util.hasPermission(
    user,
    "org.manageChallenges",
    challenge?._id,
  );
  const canViewIdeas = util.hasPermission(
    user,
    "challenge.viewIdeas",
    challenge?._id,
  );
  const canViewProjectBoard = util.hasPermission(
    user,
    "challenge.viewProjectBoard",
    challenge?._id,
  );
  const canViewImpacts = util.hasPermission(
    user,
    "challenge.viewImpacts",
    challenge?._id,
  );
  const canViewAssessments = util.hasPermission(
    user,
    "challenge.viewAssessments",
    challenge?._id,
  );
  const anyIdeaHasAssessments = ideas.some(
    (idea) => idea.assessments && idea.assessments.length > 0,
  );

  const queryParams = useIdeaQueryParams("list");
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [challenge._id, queryParams]),
  );

  const usedVotes =
    !!challenge.voteLimit &&
    parseInt(challenge.voteLimit || "0", 10) === ideasVotedFor;

  const usesImpactStatementSystem =
    user?.ownerOrganisation?.enabledFeatures?.includes("impactStatements");

  const displayFields =
    challenge?.ownerIdeaTemplate?.body
      ?.filter((section) => section.type !== "assessment")
      .flatMap((section) =>
        section.fields.filter((field) => field.isDisplayField),
      ) || [];

  const anyIdeaHasDisplayFields = ideas.some((idea) =>
    displayFields.some((field) => idea.templated?.[field.id]),
  );

  if (ideas.length === 0) {
    return null;
  }

  return (
    <div>
      <ConfigurableTable
        loading={loading}
        tableKey="ideaTableView"
        data={ideas}
        actions={ideaActions}
        selectedKeys={selectedIdeas}
        onRowClick={(item) => {
          setPreviewOpen(item._id);
        }}
        getCellStyle={(col, item) => {
          if (!item.isPinned || col.key !== "__select") {
            return {};
          }

          return { boxShadow: "inset 3px 0 0 0 #a7cc8d" };
        }}
        onSelect={(item) => {
          if (item === null) {
            if (selectedIdeas.length === ideas.length) {
              onSelect([]);
            } else {
              onSelect(ideas);
            }
            return;
          }
          if (selectedIdeas.includes(item._id)) {
            onSelect((prev) => prev.filter((i) => i._id !== item._id));
          } else {
            onSelect((prev) => [
              ...prev.filter((i) => i._id !== item._id),
              item,
            ]);
          }
        }}
        columns={[
          {
            key: "image",
            name: "",
            settingName: "Cover image",
            width: 40,
            render: ({ item }) => (
              <CoverImageCell>
                <ImageWithFallback
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 4,
                    objectFit: "cover",
                  }}
                  src={ideaCoverImage(item)}
                  fallbackSrc={ideaCoverImage()}
                />
                {(item.isPinned || item.submissionDeadline) && (
                  <CoverMetaPins>
                    {item.isPinned ?
                      <Icon
                        name="pin"
                        className="pastel"
                        circular
                        inverted
                        color="green"
                        size="tiny"
                      />
                    : null}
                    {item.submissionDeadline ?
                      <Popup
                        mountNode={document.getElementById(
                          "semantic-modal-mount-node",
                        )}
                        offset={[-14, 0]}
                        trigger={
                          <Icon
                            circular
                            inverted
                            name="calendar"
                            className="pastel"
                            color={
                              (
                                isBefore(
                                  item.submissionDeadline.deadline,
                                  new Date(),
                                )
                              ) ?
                                "red"
                              : "yellow"
                            }
                            size="tiny"
                          />
                        }
                        content={`This ${t("generic.idea")} ${isBefore(item.submissionDeadline.deadline, new Date()) ? "had" : "has"} a custom deadline: ${formatDate(item.submissionDeadline.deadline)}`}
                      />
                    : null}
                  </CoverMetaPins>
                )}
              </CoverImageCell>
            ),
          },
          {
            key: "name",
            width: 400,
            name: t("common:capitalise", { key: "generic.ideas" }),
            render: ({ item }) => (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {item.children ?
                    <Icon name="sitemap" size="small" />
                  : null}
                  <IdeaName
                    tabIndex={0}
                    aria-role="button"
                    onKeyDown={handleClickForKeyboard(() =>
                      setPreviewOpen(item._id),
                    )}
                    style={{
                      fontWeight: item.currentUserHasViewed ? "400" : "700",
                    }}
                  >
                    {item.name}
                  </IdeaName>
                </div>
                <span>
                  {loading ?
                    <PlaceholderItem width={120} height={10} />
                  : <>
                      {item.lockedOwnerIdeaBusinessProfile ?
                        <IdeaBusinessProfileChip
                          ideaBusinessProfile={
                            item.lockedOwnerIdeaBusinessProfile
                          }
                          compact="very"
                        />
                      : <UserChip user={item.owner} compact="very" />}
                      {(
                        (item.collaborators?.length ||
                          !!item.lockedOwnerIdeaBusinessProfile) &&
                        item.authors
                      ) ?
                        <Popup
                          mountNode={document.getElementById(
                            "semantic-modal-mount-node",
                          )}
                          trigger={
                            <span>
                              &nbsp;+&nbsp;
                              {util.pluralise(
                                item.lockedOwnerIdeaBusinessProfile?.name ?
                                  (item.collaborators?.length || 0) + 1
                                : item.collaborators?.length || 0,
                                "other",
                                "others",
                              )}
                            </span>
                          }
                          content={
                            <div>
                              <h4 style={{ margin: 0 }}>Authors</h4>
                              {item.lockedOwnerIdeaBusinessProfile ?
                                <IdeaBusinessProfileChip
                                  ideaBusinessProfile={
                                    item.lockedOwnerIdeaBusinessProfile
                                  }
                                  compact="very"
                                />
                              : null}
                              {item.authors?.map((author) => (
                                <UserChip
                                  key={author._id}
                                  user={author}
                                  compact="very"
                                />
                              ))}
                            </div>
                          }
                        />
                      : null}
                    </>
                  }
                </span>
              </div>
            ),
          },
          {
            key: "status",
            name: "Status",
            render: ({ item }) => {
              return item.isSubmitted ?
                  <div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <CircularProgress
                        progress={1}
                        size={14}
                        strokeWidth={7}
                        color="#c6ebad"
                        style={{ marginTop: -2 }}
                      />
                      <span>Submitted</span>
                    </div>
                    <div style={{ fontSize: 12, color: "gray" }}>
                      on {formatDate(item.submittedAt, "dd/MM/yy")}
                    </div>
                  </div>
                : <div>
                    <Popup
                      on="hover"
                      content={`${t("common:capitalise", { key: "generic.idea" })} is ${Math.round((item.completeness || 0) * 100)}% complete`}
                      offset={[-13, 0]}
                      trigger={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <CircularProgress
                            progress={item.completeness || 0}
                            size={14}
                            strokeWidth={3}
                            color="#e6c980"
                            backgroundColor="#fbeece"
                            style={{ marginTop: -2 }}
                          />
                          <span>Draft</span>
                        </div>
                      }
                    />
                  </div>;
            },
          },
          {
            key: "assessment",
            name: "Assessment",
            isVisible:
              challenge.ownerIdeaTemplate?.assessmentEnabled &&
              (canViewAssessments ||
                canManageChallenges ||
                anyIdeaHasAssessments),
            render: ({ item }) => {
              if (loading) {
                return <PlaceholderItem width={60} height={30} />;
              }
              return (
                <IdeaAssessmentProgress idea={item} challenge={challenge} />
              );
            },
          },
          {
            key: "projectLane",
            name: "Project lane",
            width: 125,
            isVisible: canManageChallenges || canViewProjectBoard,
            render: ({ item }) => {
              const pmBoardObject =
                item.projectManagement?.boards?.length ?
                  item.projectManagement.boards.find(
                    (b) => b.forId === challenge?._id,
                  )
                : null;

              return (
                <span>{pmBoardObject?.laneObject?.name || "Default"}</span>
              );
            },
          },
          {
            key: "labels",
            name: "Labels",
            isVisible: canManageChallenges || canViewIdeas,
            render: ({ item }) => {
              return (
                <div
                  style={{
                    display: "grid",
                    gridTemplateRows: "1fr 1fr",
                    gridAutoColumns: "auto",
                    gridAutoFlow: "column",
                    gap: "4px 2px",
                  }}
                >
                  {item.labels?.map((label) => (
                    <LabelItem key={label} label={label} />
                  ))}
                </div>
              );
            },
          },
          {
            key: "displayFields",
            name: t("common:capitalise", { key: "generic.idea" }) + " content",
            width: 135,
            isVisible: anyIdeaHasDisplayFields,
            render: ({ item }) => {
              const displayFieldsValues = displayFields
                .map((field) => ({
                  id: field.id,
                  value: item.templated?.[field.id],
                  title: field.title,
                  type: field.type,
                }))
                .filter((field) => field.value);

              return <IdeaDisplayFields fields={displayFieldsValues} />;
            },
          },
          {
            key: "impacts",
            name: t("common:capitalise", { key: "generic.impacts" }),
            width: 200,
            isVisible:
              (canViewImpacts || canManageChallenges) &&
              usesImpactStatementSystem &&
              !!challenge.impactStatements?.enabled,
            render: ({ item }) => {
              const ideaImpacts =
                ideaImpactsQuery.data?.ideaImpacts?.[item._id];
              return (
                <LaneCardImpacts
                  showUserEstimate={!!challenge?.impactStatements?.showOnIdea}
                  impactData={ideaImpacts}
                />
              );
            },
          },
          {
            key: "likes",
            name: "Likes",
            isVisible: challenge?.votingVisibility === "users",
            render: ({ item }) => {
              if (loading) {
                return <PlaceholderItem width={60} height={30} />;
              }

              return (
                <IdeaLikeHandler
                  idea={item}
                  challenge={challenge}
                  onLikeIdea={(ideaId, addVote) => voteForIdea(item, addVote)}
                  trigger={
                    <TooltipButton
                      size="tiny"
                      disabled={usedVotes && !item.votedFor}
                      tooltip={
                        usedVotes && !item.votedFor ?
                          "You have used all your votes"
                        : undefined
                      }
                      icon="thumbs up"
                      onClick={() => setPreviewOpen(item._id)}
                      color={item.votedFor ? "green" : "grey"}
                      label={
                        (
                          canManageChallenges ||
                          util.hasPermission(
                            user,
                            "challenge.viewIdeas",
                            challenge?._id,
                          ) ||
                          challenge?.voteCountVisibility === "users"
                        ) ?
                          {
                            basic: true,
                            pointing: "left",
                            content: item.voteCount || 0,
                          }
                        : null
                      }
                    />
                  }
                />
              );
            },
          },
          {
            key: "comments",
            name: "Comments",
            isVisible: !challenge?.preventIdeaComments,
            render: ({ item }) => {
              return (
                <Button
                  size="tiny"
                  icon="comments"
                  color={item.commentCount ? "blue" : "grey"}
                  style={{ margin: 0 }}
                  onClick={() => {
                    setPreviewOpen(item._id, { toComments: true });
                  }}
                  label={{
                    basic: true,
                    pointing: "left",
                    content: item.commentCount || 0,
                  }}
                />
              );
            },
          },
          {
            key: "tags",
            name: "Tags",
            render: ({ item }) => {
              return (
                <>
                  <TagModalChooser
                    hideView={true}
                    isOpen={tagsOpen === item._id}
                    onClose={() => setTagsOpen("")}
                    tags={item.ownerTags}
                    availableTags={challenge.tagList.filter(
                      (tag) => !tag?.disabledEntities?.includes("ideas"),
                    )}
                    addTag={(tag) => addTag(item, tag)}
                    removeTag={(tag) => removeTag(item, tag)}
                    canEdit={
                      util.canManageChallenge(user, challenge) ||
                      util.hasPermission(
                        user,
                        "challenge.editIdeaTags",
                        challenge?._id,
                      )
                    }
                  />
                  <Popup
                    mountNode={document.getElementById(
                      "semantic-modal-mount-node",
                    )}
                    on="click"
                    trigger={
                      <Button
                        size="tiny"
                        style={{ margin: 0 }}
                        icon="hashtag"
                        label={{
                          basic: true,
                          pointing: "left",
                          content: item.tags?.length ?? 0,
                        }}
                      />
                    }
                    content={
                      <div>
                        <h4>{t("generic.tags")}</h4>
                        <TagView tags={item.ownerTags || []} />
                        {(
                          util.canManageChallenge(user, challenge) ||
                          util.hasPermission(
                            user,
                            "challenge.editIdeaTags",
                            challenge?._id,
                          )
                        ) ?
                          <Button
                            compact
                            icon="hashtag"
                            content={t("tags.edit")}
                            size="tiny"
                            style={{ marginLeft: 3 }}
                            onClick={() => {
                              setTagsOpen(item._id);
                            }}
                          />
                        : null}
                      </div>
                    }
                  />
                </>
              );
            },
          },
        ]}
      />
    </div>
  );
};

export default IdeaTableView;
